import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Provider } from 'react-redux';
import { compose } from 'redux';
import { useFormik } from 'formik';
import { isNil } from 'ramda';
import { toast } from 'react-toastify';
import { Checkbox } from '@material-ui/core';
import moment from 'moment';
import { CheckBoxOutlineBlankRounded } from '@material-ui/icons';

import store from '../../../store';
import { multibonusRestHooks } from '../../../store/Multibonus/MultibonusSlice';
import { messagesRestHooks } from '../../../store/Messages/MessagesSlice';
import { userRestHooks } from '../../../store/User/UserSlice';
import FormHeader from '../../../components/FormHeader';
import Label from '../../../components/Label';
import Group from '../../../components/Group';
import Column from '../../../components/Column';
import Button from '../../../components/Button';
import Row from '../../../components/Row';
import Select from '../../../components/Select';
import { ErrorFlash } from '../../../components';
import StoreSelect from '../../../components/StoreSelect';
import DatePickerTimeZone from '../../../components/DatePickerTimeZone';
import { useFetchStatus } from '../../../utils/fetchStatusUtils';
import {
  validationSchema,
  initialValues,
} from '../../../forms/Bonus/multibonusForm';
import MessageSelect from '../../../containers/SendMessageContainer/components/SendMessageForm/components/MessageSelect';
import UserSelect from '../components/UserSelect';
import CohortSelect from '../components/CohortSelect';
import { storesRestHooks } from '../../../store/Stores/StoresSlice';
import { cohortsRestHooks } from '../../../store/Cohorts/CohortsSlice';
import { STORE_DEFAULT } from '../../constants';

function Multibonus(props) {
  const { createMultibonus, multibonus } = multibonusRestHooks.use();

  const {
    loadMessages,
    loadResetMessages,
    messages: { loadingStatus, items: messages },
  } = messagesRestHooks.use();

  const {
    loadUsers,
    users: { loadingStatus: loadingStatusUsers, items: users },
  } = userRestHooks.use();

  const {
    loadCohorts,
    loadResetCohorts,
    cohorts: { loadingStatus: loadingStatusCohorts, items: cohorts },
  } = cohortsRestHooks.use();

  const {
    loadStores,
    stores: { loadingStatus: storesLoadingState, items: stores },
  } = storesRestHooks.use();

  const [emails, setEmails] = useState([]);
  const [userModalOpen, setUserModal] = useState(false);
  const [cohortModalOpen, setCohortModal] = useState(false);
  const [cohortSelect, setCohort] = useState();
  const [scheduling, setScheduling] = useState(false);

  const { isFulfilled } = useFetchStatus(loadingStatus);
  const history = useHistory();

  const notify = () =>
    toast.success('Bonus Successfully Created!', {
      position: 'top-right',
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });

  const notifyError = (message) =>
    toast.error(message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });

  const newBonus = async (values, actions) => {
    const { messageId, amount, description, storeIdEq, comment, scheduledAt } =
      values;
    const users = emails.map((item) => {
      return item.value;
    });

    const body = {
      messageId: messageId,
      amount: Number(amount),
      description: description,
      users: users,
      storeId: storeIdEq,
      auditComment: comment,
      mixpanelCohortId: cohortSelect?.id,
      scheduledAt: scheduledAt,
      userCount: users.length > 0 ? users.length : cohortSelect.count,
    };

    if (!cohortSelect && users.length <= 0) {
      notifyError('You must choose an existing cohort or make a list of users');
      return;
    }

    await createMultibonus(body);
    actions.resetForm();
    setEmails([]);
    if (isNil(multibonus.errors)) {
      history.goBack();
      notify();
    }
  };

  const formik = useFormik({
    initialValues: initialValues(messages, stores),
    onSubmit: newBonus,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema,
  });

  const { values, handleSubmit, setFieldValue, errors, setErrors } = formik;

  const handleChange = ({ target }) => {
    const { id, value } = target;
    setFieldValue(id, value);
  };

  const handleAddEmail = (user) => {
    setFieldValue('email', '');
    setEmails([...emails, { value: user.auth0Id, label: user.email }]);
    setCohort();
  };

  const handleAddCohort = (cohort) => {
    setCohort(cohort);
    setEmails([]);
  };

  const handleSearch = async () => {
    await loadUsers({ email: values.email });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleRemoveEmail = (id) => {
    const newPeople = emails.filter((item) => item.value !== id);
    setEmails([...newPeople]);
  };

  useEffect(() => {
    if (loadingStatusUsers === 'fulfilled' && users.length > 0) {
      setUserModal(true);
    }
    if (loadingStatusUsers === 'fulfilled' && users.length == 0) {
      notifyError('User not found');
    }
  }, [loadingStatusUsers, users]);

  useEffect(() => {
    loadMessages();
    loadStores();
    return () => {
      loadResetMessages();
    };
  }, []);

  return (
    <div className="store-new">
      <ErrorFlash errors={multibonus.errors} message={multibonus.errors} />
      <form className="store-form" onSubmit={handleSubmit}>
        <FormHeader title="New Bonuses">
          <input
            id="saveStore"
            className="store-submit"
            type="submit"
            defaultValue="Save"
          />
        </FormHeader>
        <div className="form-fields">
          <Group>
            <Row>
              <div className="input">
                <Label name="email" title="Email" errors={errors} />
                <input
                  id="email"
                  value={values.email}
                  onChange={handleChange}
                  onKeyDown={handleKeyPress}
                />
                <UserSelect
                  onSelect={handleAddEmail}
                  users={users}
                  modalOpen={userModalOpen}
                  closeModal={() => setUserModal(false)}
                />
                <CohortSelect
                  onSelect={handleAddCohort}
                  cohorts={cohorts}
                  modalOpen={cohortModalOpen}
                  closeModal={() => setCohortModal(false)}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '20px',
                }}
              >
                <Button
                  id="archiveTeam"
                  className="button"
                  onClick={() => handleSearch()}
                >
                  Search
                </Button>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '20px',
                }}
              >
                <Button
                  id="archiveTeam"
                  className="button"
                  onClick={() => {
                    loadCohorts(), setCohortModal(true);
                  }}
                >
                  Mixpanel cohort
                </Button>
              </div>
            </Row>
            <div className="form-group-fields">
              <Column>
                <div className="input">
                  <Label name="emails" title="Sent to" errors={errors} />
                  <div>
                    {emails.length > 0 && (
                      <table className="tables">
                        <thead>
                          <tr>
                            <th>Email</th>
                            <th>Auth0 Id</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {emails.map((item) => (
                            <tr>
                              <td>{item.label}</td>
                              <td>{item.value}</td>
                              <td
                                className="delete-button"
                                onClick={() => handleRemoveEmail(item.value)}
                              >
                                X
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {cohortSelect && (
                      <table className="tables">
                        <thead>
                          <tr>
                            <th>Cohort Id</th>
                            <th>Cohort Name</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{cohortSelect.id}</td>
                            <td>{cohortSelect.name}</td>
                            <td
                              className="delete-button"
                              onClick={() => setCohort()}
                            >
                              X
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </Column>
              <Column>
                <div className="input">
                  <Label name="amount" title="Amount" errors={errors} />
                  <input
                    id="amount"
                    value={values.amount}
                    onChange={handleChange}
                  />
                </div>
                <div className="input">
                  <Label
                    name="description"
                    title="Description"
                    errors={errors}
                  />
                  <input
                    id="description"
                    value={values.description}
                    onChange={handleChange}
                  />
                </div>
                <div className="input">
                  <Label
                    name="comment"
                    title="Comment (for audit purposes)"
                    errors={errors}
                  />
                  <input
                    id="comment"
                    value={values.comment}
                    onChange={handleChange}
                  />
                </div>
                <div className="input">
                  <Label name="storeIdEq" title="Store" errors={errors} />
                  <StoreSelect
                    stores={stores}
                    onChange={handleChange}
                    value={values.storeIdEq}
                    loadingState={storesLoadingState}
                    defaultOption={
                      stores
                        ?.filter((item) => item?.name?.includes(STORE_DEFAULT))
                        .map((item) => ({
                          value: item.id,
                          label: item.name,
                        }))[0]
                    }
                  />
                </div>
                <div className="input">
                  <MessageSelect
                    value={values.messageId}
                    onChange={handleChange}
                    messages={messages}
                    isFulfilled={isFulfilled}
                    errors={errors}
                    required={false}
                  />
                </div>
                <div className="input">
                  <Label
                    name="scheduledAt"
                    title="Scheduling"
                    errors={errors}
                  />
                  <input
                    type="checkbox"
                    checked={scheduling}
                    onChange={() => setScheduling(!scheduling)}
                    id="scheduling"
                  />
                  {scheduling && (
                    <DatePickerTimeZone
                  
                      id="scheduledAt"
                      onChange={handleChange}
                      value={values.scheduledAt}
                    />
                  )}
                </div>
              </Column>
            </div>
          </Group>
        </div>
      </form>
    </div>
  );
}
const addStore = (ProxyComponent) => (props) =>
  (
    <Provider store={store}>
      <ProxyComponent {...props} />
    </Provider>
  );

export default compose(addStore)(Multibonus);
